import {AnimatePresence, m} from 'framer-motion'
import _ from 'lodash'
import {FC, PropsWithChildren} from 'react'

import {Button} from '@/components/common/content/Button'
import {ButtonSize, ButtonVariant} from '@/components/common/content/ButtonTypes'
import {useCommonContent} from '@/content/common'
import {useTranslation} from '@/i18n'
import {IconSize} from '@/theme/icons'
import {HEADER_HEIGHT_MOBILE} from '@/theme/sizing'
import {theme} from '@/theme/theme'
import {useUniqueClassName} from '@/utils/hooks/useUniqueClassName'
import {TABLET_SCREENSIZE} from '@/utils/styles/breakpoints'

import {Text, TextFontWeight, TextVariant} from '../common/content/Text'
import {CloseIcon} from '../common/icons/CloseIcon'
import {MenuIcon} from '../common/icons/MenuIcon'

type MobileNavigationProps = {
  isActive: boolean
  toggleNavigation: () => void
  title?: string
}

export const MobileNavigation: FC<PropsWithChildren<MobileNavigationProps>> = ({
  isActive,
  toggleNavigation,
  title,
  children,
}) => {
  const {t} = useTranslation()
  const {menuLabel} = useCommonContent()
  const className = useUniqueClassName('mobile-navigation')

  return (
    <>
      <div className={className}>
        <Button
          type="button"
          onClick={toggleNavigation}
          variant={ButtonVariant.darkGray}
          size={ButtonSize.medium}
          // The only content is an icon, so without this the button has no accessible name
          ariaLabel={t(menuLabel)}
          ariaExpanded={isActive}
        >
          {isActive ? (
            <CloseIcon size={IconSize.medium} color={theme.palette.text.white} />
          ) : (
            <MenuIcon />
          )}
        </Button>
        <AnimatePresence>
          {isActive && (
            <m.div
              className={`${className}-menu`}
              initial={{scaleY: 0, opacity: 0}}
              animate={{
                scaleY: 1,
                opacity: 1,
                transition: {type: 'spring', stiffness: 300, damping: 30},
              }}
              exit={{scaleY: 0, opacity: 0, transition: {duration: 0.2, ease: 'easeInOut'}}}
              style={{originY: 1}}
              onUpdate={_.noop}
            >
              {title && (
                <div className={`${className}-title`}>
                  <Text
                    variant={TextVariant.subtitle12}
                    color={theme.palette.text.white}
                    fontWeight={TextFontWeight.semibold}
                    align="center"
                    className="text-block"
                  >
                    {title}
                  </Text>
                </div>
              )}
              {children}
            </m.div>
          )}
        </AnimatePresence>
      </div>

      <style jsx global>{`
        .${className} {
          display: none;
        }

        @media ${TABLET_SCREENSIZE} {
          .${className} {
            display: block;
          }

          .${className}-menu {
            position: absolute;
            left: 0;
            right: 0;
            z-index: 2;
            background-color: ${theme.palette.tertiary.main};
            border: 1px solid ${theme.palette.gray[600]};
            bottom: calc(100% + ${theme.spacing[1]});
            display: flex;
            flex-direction: column;
            padding: ${theme.spacing[3]} ${theme.spacing[5]} ${theme.spacing[5]};
            border-radius: 20px;
            overflow-y: auto;
            max-height: calc(100vh - ${HEADER_HEIGHT_MOBILE} - ${theme.spacing[3]});
            max-height: calc(100svh - ${HEADER_HEIGHT_MOBILE} - ${theme.spacing[3]});
          }

          .${className}-menu::-webkit-scrollbar-track {
            width: 4px;
            background-color: ${theme.palette.gray[600]};
          }

          .${className}-menu::-webkit-scrollbar {
            width: 4px;
            background-color: ${theme.palette.gray[600]};
          }

          .${className}-menu::-webkit-scrollbar-thumb {
            width: 4px;
            border-radius: 8px;
            background-color: ${theme.palette.gray[300]};
          }

          .${className}-title {
            margin-top: ${theme.spacing[2]};
          }

          body #missive-webchat,
          body #missive-chat-launcher {
            z-index: ${isActive ? 1 : 10} !important;
          }
        }
      `}</style>
    </>
  )
}
